import React, { useCallback, useEffect, useState } from 'react';
import { Table, Button, Modal, Popconfirm, message, Tag, Row, Col, Input, Space } from 'antd';
import { getWhatsNews, deleteWhatsNews } from '../utils/apiClient';
import WhatsNewsForm from '../components/WhatsNewsForm';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { AppSelector } from '../components/AppSelector';

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

const WhatsNews = () => {
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (categoryFilter) params.category = categoryFilter;
      const res = await getWhatsNews(params);
      setData(res.data);
    } catch (err) {
      message.error('Failed to fetch data');
    }
    setLoading(false);
  }, [searchText, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [searchText, categoryFilter, fetchData]);

  const handleAdd = () => {
    setEditingItem(null);
    setModalVisible(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteWhatsNews(id);
      message.success('Deleted successfully');
      fetchData();
    } catch {
      message.error('Delete failed');
    }
  };

  const handleModalClose = (refresh) => {
    setModalVisible(false);
    setEditingItem(null);
    if (refresh) fetchData();
  };

  const columns = [
    { title: 'App', dataIndex: 'category', key: 'category' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => tags?.split(',').map((tag) => <Tag key={tag}>{tag.trim()}</Tag>),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc) => {
        const clean = stripHtml(desc || '');
        return clean.length > 500 ? clean.slice(0, 500) + '…' : clean;
      },
    },
    { title: 'Title', dataIndex: 'title', key: 'title' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this item?" onConfirm={() => handleDelete(record._id)}>
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  const handleSearch = () => {
    fetchData();
  };

  const handleReset = () => {
    setSearchText('');
    setCategoryFilter('');
  };

  return (
    <div style={{ padding: 24 }}>
      <h2>Manage What&apos;s News</h2>
      <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16 }}>
        Add New
      </Button>
      <div className="table-actions">
        <Row gutter={[16, 16]} style={{ width: '100%' }}>
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xs={24} sm={8} md={4}>
            <AppSelector value={categoryFilter || undefined} onChange={setCategoryFilter} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                Search
              </Button>
              <Button onClick={handleReset}>Reset</Button>
              <Button icon={<ReloadOutlined />} onClick={() => fetchData()} title="Refresh" />
            </Space>
          </Col>
        </Row>
      </div>
      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} />
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        title={editingItem ? "Edit What's News" : "Add What's News"}
      >
        <WhatsNewsForm item={editingItem} onClose={handleModalClose} />
      </Modal>
    </div>
  );
};

export default WhatsNews;

